import { Dimensions, Metrics, Sources } from 'components/insights/constants';
import { colors } from 'styles/theme/colors';

import { ReferralAnalyticsQueryParams } from './query/useReferralAnalytics';

export type ViewByOption = {
  label: string;
  value: ViewByValue;
  dimension: Dimensions;
};

export const VIEW_BY = {
  SENDING_GROUP: 'sendingGroup',
  RECEIVING_CLIENT: 'receivingClient',
  HEALTH_PLAN: 'healthPlan',
} as const;

export type ViewByValue = (typeof VIEW_BY)[keyof typeof VIEW_BY];

export const STATUS_COLORS: Record<string, string> = {
  accepted: colors.accentGreen,
  rejected: colors.accentRed,
  canceled: colors.black50,
  pending: colors.black10,
};

export const REFERRAL_STATUS_LABELS: Record<string, string> = {
  accepted: 'Accepted',
  rejected: 'Denied',
  canceled: 'Canceled',
  pending: 'Pending',
};

export const REFERRAL_STATUSES = Object.keys(REFERRAL_STATUS_LABELS);

export const VIEW_BY_DIMENSION_MAP: Record<ViewByValue, Dimensions> = {
  sendingGroup: Dimensions.SENDING_GROUP,
  receivingClient: Dimensions.RECEIVING_CLIENT,
  healthPlan: Dimensions.HEALTH_PLAN_CLASSIFICATION,
};

const SENDING_DIMENSIONS = new Set([Dimensions.SENDING_GROUP]);
const RECEIVING_DIMENSIONS = new Set([Dimensions.RECEIVING_CLIENT, Dimensions.RECEIVING_GROUP]);
const PLAN_DIMENSIONS = new Set([Dimensions.HEALTH_PLAN_CLASSIFICATION, Dimensions.PLAN_TYPE_CLASSIFICATION]);

export const getReferralCountLabel = (dimension: Dimensions): string => {
  if (SENDING_DIMENSIONS.has(dimension)) return 'Referrals Sent';
  if (RECEIVING_DIMENSIONS.has(dimension)) return 'Referrals Received';
  if (PLAN_DIMENSIONS.has(dimension)) return 'Total Referrals';
  return 'Referrals Sent';
};

export const getEntityColumnHeader = (
  dimension: Dimensions,
  {
    sendingGroupTypeName,
    receivingGroupTypeName,
  }: { sendingGroupTypeName?: string; receivingGroupTypeName?: string } = {}
): string => {
  switch (dimension) {
    case Dimensions.SENDING_GROUP:
      return `Referring ${sendingGroupTypeName ?? ''}`.trim();
    case Dimensions.RECEIVING_CLIENT:
      return `${receivingGroupTypeName ?? ''} Company`.trim();
    case Dimensions.RECEIVING_GROUP:
      return receivingGroupTypeName ?? '';
    case Dimensions.HEALTH_PLAN_CLASSIFICATION:
      return 'Plan';
    case Dimensions.PLAN_TYPE_CLASSIFICATION:
      return 'Plan Type';
    default:
      return '';
  }
};

export const formatResponseTime = (seconds: number | null | undefined): string => {
  if (seconds == null || isNaN(seconds)) return '—';
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0 && minutes === 0) return '<0h 1m';
  return `${hours}h ${minutes}m`;
};

export const formatDaysValue = (days: number | null | undefined): string => {
  if (days == null || isNaN(days)) return '—';
  return `${days.toFixed(1)} days`;
};

export const formatPercentage = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '—';
  return `${value.toFixed(1)}%`;
};

export const validMetricValue = (value: number | null | undefined): number | null =>
  value != null && value >= 0 ? value : null;

const VIEW_BY_DIMENSIONS = new Set(Object.values(VIEW_BY_DIMENSION_MAP));

const REFERRAL_METRICS = [
  Metrics.REVIEW_ID_COUNT,
  Metrics.LOCATION_EPISODE_ID_COUNT,
  Metrics.ACCEPTANCE_RATE,
  Metrics.DENIAL_RATE,
  Metrics.CANCEL_RATE,
  Metrics.PENDING_RATE,
  Metrics.RESPONSE_TIME_SECONDS_AVERAGE,
  Metrics.CREATED_TO_DISCHARGE_DAYS_AVERAGE,
];

export const getReferralDetailsParams = (dimension: Dimensions): ReferralAnalyticsQueryParams => ({
  source: Sources.INSIGHTS_REFERRALS,
  dimensions: [dimension, Dimensions.REFERRAL_STATUS],
  metrics: REFERRAL_METRICS,
  rollups: true,
});

export const getReferralExportParams = (dimension: Dimensions): ReferralAnalyticsQueryParams => ({
  source: Sources.INSIGHTS_REFERRALS,
  dimensions: [dimension],
  metrics: REFERRAL_METRICS,
});

export const withDimensionIds = (params: ReferralAnalyticsQueryParams): ReferralAnalyticsQueryParams => ({
  ...params,
  dimensions: params.dimensions?.map((d) => (VIEW_BY_DIMENSIONS.has(d as Dimensions) ? `${d}:with_id` : d)),
});
